import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);

const getTieredPrice = (product, qty = 1) => {
  const tiers = product.priceTiers || [];
  const match = tiers.find((tier) => qty >= tier.min && (tier.max === null || tier.max === undefined || qty <= tier.max));
  return match ? match.price : product.price;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cb_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('cb_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        const nextQty = existing.qty + qty;
        return prev.map((i) => (
          i.id === product.id ? { ...i, qty: nextQty, price: getTieredPrice(product, nextQty), priceTiers: product.priceTiers || i.priceTiers || [] } : i
        ));
      }
      return [...prev, { id: product.id, name: product.name, price: getTieredPrice(product, qty), image: product.image, thc: product.thc, qty, priceTiers: product.priceTiers || [] }];
    });
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const updateQty = (id, qty) => {
    if (qty <= 0) return removeItem(id);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty, price: getTieredPrice(i, qty) } : i)));
  };
  const clearCart = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const total = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);

  const value = { items, addItem, removeItem, updateQty, clearCart, count, total, getTieredPrice };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
